// Standard Do's & Don'ts every brief must contain (per CET PRD). Locked/read-only
// in the UI. campaignDos/campaignDonts are persisted as plain string arrays with
// no per-item metadata, so defaults are recognised by a normalized key rather
// than exact text: saved briefs keep matching after punctuation/casing/spacing
// tweaks to the wording below, and are rewritten to the current wording on load.
export const DEFAULT_CAMPAIGN_DOS = [
	"Ensure you mention brand and show visual branding within the first 3 seconds",
	"Ensure the brand is easily distinguishable with logos/products facing the camera.",
	"Speak like you normally would. If a specific phrase is required, weave it in conversationally.",
	"Film in good lighting (face lit, product visible) and a clean background that doesn't distract",
	"Select locations with minimal background noise to ensure clear audio.",
	"Keep pacing tight: cut dead air, aim for one idea per beat, use captions/on-screen text for accessibility and to land the message even if muted.",
];

export const DEFAULT_CAMPAIGN_DONTS = [
	"Do not mention or show other brands",
	"Do not film in poor lighting or cluttered backgrounds",
	"Do not use coarse, offensive, or discriminatory language.",
	"Don't use copyrighted music",
];

// When a default is reworded beyond punctuation/casing/spacing, add its previous
// wording here (old text -> current text) so briefs saved with the old text are
// still recognised as that default instead of becoming an editable custom entry.
export const PREVIOUS_DEFAULT_WORDINGS: Record<string, string> = {};

// Case-, whitespace-, apostrophe- and trailing-punctuation-insensitive key.
export function briefEntryKey(entry: string): string {
	return entry
		.normalize("NFKC")
		.replace(/[‘’′]/g, "'")
		.replace(/\s+/g, " ")
		.trim()
		.replace(/[\s.!;,]+$/, "")
		.toLowerCase();
}

function resolveDefault(entry: string, defaults: string[]): string | undefined {
	const key = briefEntryKey(entry);
	const legacyKeys = Object.entries(PREVIOUS_DEFAULT_WORDINGS)
		.filter(([old]) => briefEntryKey(old) === key)
		.map(([, current]) => briefEntryKey(current));
	return defaults.find((d) => {
		const dKey = briefEntryKey(d);
		return dKey === key || legacyKeys.includes(dKey);
	});
}

// Canonical list: every standard entry first, in its current wording and order,
// followed by the brief's custom entries. Stored entries that match a default
// (under any past wording) collapse into it rather than surviving as duplicates.
export function withDefaults(items: string[], defaults: string[]): string[] {
	const custom = items.filter((item) => resolveDefault(item, defaults) === undefined);
	return [...defaults, ...custom];
}

// Whether the entry at `idx` of a withDefaults-normalized list is a locked default.
// Positional so a custom entry being typed never locks itself mid-edit by
// happening to match a default.
export function isLockedDefault(items: string[], idx: number, defaults: string[]): boolean {
	return idx < defaults.length && briefEntryKey(items[idx]) === briefEntryKey(defaults[idx]);
}
